/**
 * Client-side binding for the getDvfComparables Cloud Function + cache.
 *
 * **Cache par fiche** (`/users/{uid}/fiches/{fid}/dvf`) : CEREMA DVF+ n'est
 * publié que 2 fois par an, et la CF est l'opération la plus chère du
 * pipeline côté CPU (filtrage + stats sur 100+ transactions bbox). Pas la
 * peine de la rappeler à chaque ouverture de fiche.
 *
 * Protocole lecture :
 *   1. Read cache Firestore.
 *   2. Si frais (< 90 j) et inputsHash identique → retourne le cache.
 *   3. Sinon → appel CF. Sur succès, overwrite cache. Sur 503 avec un
 *      cache stale dispo, on sert le stale (résilience — mieux vaut une
 *      vieille médiane que pas de médiane).
 *
 * Le hook `useDvfComparables` et `fetchDvfComparablesCached` consomment
 * tous deux cette logique — PDF et UI voient le même cache.
 */

import { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import {
  doc,
  getDoc,
  setDoc,
  Timestamp,
  type Unsubscribe,
} from "firebase/firestore";
import { auth, db, functions } from "./firebase";
import type { DvfComparables } from "./types";

type Input = {
  lat: number;
  lng: number;
  propertyType: "Maison" | "Appartement" | "Immeuble" | "Terrain" | null;
  surfaceBatiM2: number | null;
};

const callDvf = httpsCallable<Input, DvfComparables>(
  functions,
  "getDvfComparables",
);

// ─── Cache config ────────────────────────────────────────────────────────

const CACHE_TTL_DAYS = 90;
const CACHE_TTL_MS = CACHE_TTL_DAYS * 24 * 60 * 60 * 1000;

type CachedDvf = {
  comparables: DvfComparables;
  fetchedAt: Timestamp;
  inputsHash: string; // pour invalider si un input change (surface, type)
};

function inputsHashOf(input: Input): string {
  // On clamp lat/lng à 4 décimales (~11 m) pour que de micro-jitters
  // BAN ne cassent pas le cache. Le backend ajuste aussi à cette
  // résolution pour son propre cache par grille.
  const lat = input.lat.toFixed(4);
  const lng = input.lng.toFixed(4);
  const type = input.propertyType ?? "*";
  // Surface arrondie à 5 m² : une maison passée de 220 à 221 m² ne
  // devrait pas invalider le cache (le filtre DVF tolère une fourchette
  // ±30 %).
  const surf = input.surfaceBatiM2 != null
    ? Math.round(input.surfaceBatiM2 / 5) * 5
    : "null";
  return `${lat}|${lng}|${type}|${surf}`;
}

// ─── Raw CF call (public — utilisé en fallback cache miss) ──────────────

export async function fetchDvfComparables(
  input: Input,
): Promise<DvfComparables> {
  const res = await callDvf(input);
  return res.data;
}

// ─── Cached fetcher (one-shot, utilisé par PDF + UI) ─────────────────────

/**
 * Version cached : check Firestore d'abord, fallback CF sinon.
 * Nécessite l'id de la fiche + l'auth actif (sinon skip cache direct CF).
 *
 * Si `forceRefresh` = true, invalide et recompute.
 */
export async function fetchDvfComparablesCached(
  ficheId: string,
  input: Input,
  forceRefresh = false,
): Promise<DvfComparables> {
  const user = auth.currentUser;
  if (!user) {
    // Pas d'utilisateur → skip le cache, appel direct.
    return fetchDvfComparables(input);
  }
  const uid = user.uid;
  const ref = doc(db, "users", uid, "fiches", ficheId, "dvf", "latest");
  const wantedHash = inputsHashOf(input);

  // 1. Lecture cache.
  let cached: CachedDvf | null = null;
  if (!forceRefresh) {
    try {
      const snap = await getDoc(ref);
      if (snap.exists()) {
        cached = snap.data() as CachedDvf;
        const age = Date.now() - cached.fetchedAt.toMillis();
        if (cached.inputsHash === wantedHash && age < CACHE_TTL_MS) {
          return cached.comparables;
        }
      }
    } catch (e) {
      // Erreur réseau / permission → on continue vers la CF.
      console.warn("[dvf] cache read failed, falling through", e);
    }
  }

  // 2. Call CF.
  try {
    const fresh = await fetchDvfComparables(input);
    // 3. Écrit le cache en fire-and-forget (on ne bloque pas le retour
    //    si l'écriture échoue).
    const payload: CachedDvf = {
      comparables: fresh,
      fetchedAt: Timestamp.now(),
      inputsHash: wantedHash,
    };
    setDoc(ref, payload).catch((e) => {
      console.warn("[dvf] cache write failed (non-blocking)", e);
    });
    return fresh;
  } catch (err) {
    // 4. CF down (503, network, etc.) — si on a un cache stale, on le
    //    renvoie plutôt que de fail. Mieux vaut un DVF vieux de 6 mois
    //    qu'un placeholder "données non disponibles".
    if (cached) {
      console.warn(
        "[dvf] CF failed, serving stale cache",
        err instanceof Error ? err.message : err,
      );
      return cached.comparables;
    }
    throw err;
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────

type State =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; comparables: DvfComparables }
  | { status: "error"; message: string };

export function useDvfComparables(
  input: (Input & { ficheId?: string }) | null,
): State {
  const [state, setState] = useState<State>({ status: "idle" });

  useEffect(() => {
    if (!input) {
      setState({ status: "idle" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    const fetcher = input.ficheId
      ? fetchDvfComparablesCached(input.ficheId, input)
      : fetchDvfComparables(input);
    fetcher
      .then((c) => {
        if (cancelled) return;
        setState({ status: "ready", comparables: c });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setState({ status: "error", message: msg });
      });
    return () => {
      cancelled = true;
    };
    // We depend on primitive input fields to avoid re-fetching on every
    // object identity change.
  }, [
    input?.ficheId,
    input?.lat,
    input?.lng,
    input?.propertyType,
    input?.surfaceBatiM2,
  ]);

  return state;
}

// Unused Unsubscribe export reminder — kept imported for future pattern
// where we might watch the cache doc live instead of one-shot read.
export type { Unsubscribe };
